import { z } from 'zod';
import { OpaqueRef } from './ids.js';
import { Locale } from './i18n.js';
import { Identity, IsoDateTime, PositiveCents } from './primitives.js';
/**
 * The guest profile as it crosses the boundary — the projection, not the row.
 *
 * Spa "preferences" routinely carry health-adjacent information: allergies,
 * injuries, pregnancy, medication notes (agreement section 6.6). Two rules
 * follow, and both are enforced by this being a projection rather than a
 * `SELECT *`:
 *
 *  - a note the staff marked `internal` never appears here;
 *  - no financial detail beyond `lifetime_value_cents` — no payment methods, no
 *    deposits, no account credit, no discounts.
 */
export const GuestProfile = z.object({
    provider_ref: OpaqueRef,
    display_name: z.string(),
    identities: z.array(Identity),
    preferences: z.record(z.string(), z.string()).default({}),
    tags: z.array(z.string()).default([]),
    visit_count: z.number().int().min(0),
    last_visit_at: IsoDateTime.nullable(),
    lifetime_value_cents: PositiveCents,
    notes: z.string().default(''),
    /**
     * The language this guest is spoken to in (i18n-design §3) — set from
     * `Accept-Language` on the booking site, by the agent when it notices the
     * conversation language, or by staff.
     *
     * `null` when we have never learned it, which is not the same as English: an
     * agent that greets a guest in a language nobody chose for them is worse than
     * one that greets them in the tenant's own. Additive, and nullable rather than
     * defaulted for exactly that reason.
     */
    locale: Locale.nullable().default(null),
});
/** A past visit. `value_cents` is the only money that crosses (agreement G6). */
export const Visit = z.object({
    id: OpaqueRef,
    at: IsoDateTime,
    service_id: OpaqueRef.nullable(),
    service_name: z.string().nullable(),
    party: z.number().int().min(1),
    value_cents: PositiveCents,
    status: z.enum(['completed', 'cancelled', 'no_show', 'confirmed']),
    notes: z.string().default(''),
});
/** The guest as named on a booking request. */
export const GuestInput = z.object({
    name: z.string().min(1).max(200),
    identities: z.array(Identity).min(1),
    notes: z.string().max(2000).default(''),
});
//# sourceMappingURL=guest.js.map